/**
 * Wizard Progress Indicator
 *
 * Displays step progress with clickable navigation for completed steps.
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import { CheckIcon } from '@heroicons/react/24/solid';
import { useWizard } from '../context/WizardContext';
import { WIZARD_STEPS, STEP_CONFIG } from '../context/types';

/**
 * Step progress indicator showing current position in wizard.
 * Allows clicking on completed/past steps to navigate back.
 */
export function WizardProgress() {
  const { state, currentStepIndex, goToStep } = useWizard();

  return (
    <nav aria-label="Progress" className="mb-8">
      <ol className="flex items-center justify-between">
        {WIZARD_STEPS.map((step, index) => {
          const config = STEP_CONFIG[step];
          const isComplete = state.completedSteps.has(step);
          const isCurrent = state.currentStep === step;
          const isPast = index < currentStepIndex;
          const canNavigate = isPast || isComplete;

          return (
            <li key={step} className="relative flex-1">
              {/* Connector line */}
              {index < WIZARD_STEPS.length - 1 && (
                <div
                  className={`absolute top-5 left-1/2 w-full h-0.5 ${
                    isPast || isComplete ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                  aria-hidden="true"
                />
              )}

              {/* Step indicator */}
              <button
                onClick={() => canNavigate && goToStep(step)}
                disabled={!canNavigate}
                className={`relative flex flex-col items-center group ${
                  canNavigate ? 'cursor-pointer' : 'cursor-default'
                }`}
                type="button"
              >
                {/* Circle */}
                <span
                  className={`
                    w-10 h-10 flex items-center justify-center rounded-full
                    border-2 transition-colors z-10 bg-white
                    ${
                      isComplete
                        ? 'bg-blue-600 border-blue-600'
                        : isCurrent
                          ? 'border-blue-600 text-blue-600'
                          : 'border-gray-300 text-gray-500'
                    }
                    ${canNavigate && !isCurrent ? 'group-hover:border-blue-400' : ''}
                  `}
                >
                  {isComplete ? (
                    <CheckIcon className="w-5 h-5 text-white" />
                  ) : (
                    <span
                      className={`text-sm font-medium ${
                        isCurrent ? 'text-blue-600' : 'text-gray-500'
                      }`}
                    >
                      {index + 1}
                    </span>
                  )}
                </span>

                {/* Label */}
                <span
                  className={`
                    mt-2 text-xs font-medium
                    ${
                      isCurrent
                        ? 'text-blue-600'
                        : isComplete
                          ? 'text-gray-900'
                          : 'text-gray-500'
                    }
                  `}
                >
                  {config.label}
                </span>

                {/* Description (visible on larger screens) */}
                <span className="hidden sm:block mt-1 text-xs text-gray-400">
                  {config.description}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
